import { useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../components/Breadcrumb/breadcrumb';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import CheckBox from '../components/checkBox/CheckBox';
import DatePicker from '../components/datepicker/DatePicker';
import DropDown from '../components/dropdown/DropDown';
import Image from '../components/image/Image';
import Input from '../components/input/Input';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import AssetSelectionModal from '../components/modals/AssetSelectionModal';
import {
  calculateLoanInstallments,
  getAmortizationTypes,
  getPaymentFrequencyTypes,
  getRateTypes,
} from '../services/GuaranteesService';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';

interface CatalogItem {
  id: number;
  name: string;
}

interface Installment {
  installment_number: number;
  due_date: string;
  grace_only: boolean;
  amortization: number;
  interest: number;
  iva_on_interest: number;
  tax_on_capital: number;
  installment_total: number;
  remaining_amount: number;
  amortization_asset: number | null;
  interest_asset: number | null;
  iva_on_interest_asset: number | null;
  tax_on_capital_asset: number | null;
  installment_total_asset: number | null;
  remaining_amount_asset: number | null;
}

interface InstallmentPlan {
  grace_mode: string;
  total_cuotas: number;
  total_amortization: number;
  total_interest: number;
  total_amount: number;
  total_iva_on_interest: number;
  total_tax_on_capital: number;
  total_taxes: number;
  total_payable: number;
  iva_rate_applied: number;
  tax_on_capital_rate_applied: number;
  total_amortization_asset: number | null;
  total_interest_asset: number | null;
  total_amount_asset: number | null;
  total_iva_on_interest_asset: number | null;
  total_tax_on_capital_asset: number | null;
  total_payable_asset: number | null;
  installments: Installment[];
}

const MODES = [
  { key: 'PRIMERA_CUOTA_POST_GRACIA', order: 'MODO 1', label: 'Por 1ª cuota post-gracia' },
  { key: 'PRORRATEO_POST_GRACIA', order: 'MODO 2', label: 'Prorrateo post-gracia' },
  { key: 'CUOTAS_EN_GRACIA', order: 'MODO 3', label: 'Cuotas en gracia' },
  { key: 'CAPITALIZACION', order: 'MODO 4', label: 'Capitalización (compuesto)' },
];

const initialForm = {
  monetizationAmount: '10000000',
  monetizationDate: '2026-01-10',
  dueDate: '2026-08-09',
  gracePeriod: '30',
  percentagePoint: '60',
  paymentFrequencyTypeId: '1',
  amortizationTypeId: '1',
  rateTypeId: '1',
  withAsset: false,
  assetId: null as number | null,
  assetName: '',
  assetQuantity: null as number | null,
  assetQuotation: null as number | null,
};

const money = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const asset = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const COLORS = {
  surface: '#FFFFFF',
  border: '#DFE6EB',
  headerBg: '#F5F7FA',
  accent: '#0B8A6B',
  accentSoft: '#E6F5F0',
  taxColumn: '#EEF4FC',
  graceRow: '#FBF3E4',
  muted: '#5B6B7B',
};

const SURFACE_CARD: React.CSSProperties = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  // Sin box-sizing global, width 100% + padding desborda la columna de la grilla.
  boxSizing: 'border-box',
};

const toCatalogItems = (response: any): CatalogItem[] => {
  if (!response || response.success === false) return [];
  const payload = 'data' in response ? response.data : response;
  const items = Array.isArray(payload) ? payload : payload?.content;
  return Array.isArray(items) ? items : [];
};

const simulatorErrorMessage = (message?: string) => {
  if (!message) return 'No se pudo calcular el plan de cuotas.';
  return /otro|definici[oó]n manual/i.test(message)
    ? 'La opción Otro requiere definición manual por Excel.'
    : message;
};

const formatDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('es-AR');

const LoanInstallmentSimulatorPage = () => {
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [form, setForm] = useState(initialForm);
  const [catalogs, setCatalogs] = useState({
    amortization: [] as CatalogItem[],
    frequency: [] as CatalogItem[],
    rate: [] as CatalogItem[],
  });
  const [plans, setPlans] = useState<InstallmentPlan[] | null>(null);
  const [selectedMode, setSelectedMode] = useState(MODES[0].key);
  const [unit, setUnit] = useState<'money' | 'asset'>('money');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [assetModalOpen, setAssetModalOpen] = useState(false);

  useEffect(() => {
    Promise.all([getAmortizationTypes(), getPaymentFrequencyTypes(), getRateTypes()])
      .then(([amortization, frequency, rate]) => setCatalogs({
        amortization: toCatalogItems(amortization),
        frequency: toCatalogItems(frequency),
        rate: toCatalogItems(rate),
      }))
      .catch(() => setError('No se pudieron cargar los catálogos del simulador.'));
  }, []);

  const invalidate = () => {
    setPlans(null);
    setError('');
    setUnit('money');
  };

  const change = (name: keyof typeof initialForm, value: string | boolean) => {
    setForm((current) => ({ ...current, [name]: value }));
    invalidate();
  };

  const catalogName = (items: CatalogItem[], id: string) =>
    items.find((item) => String(item.id) === id)?.name ?? 'Seleccionar';

  const selectCatalog = (
    items: CatalogItem[],
    field: 'paymentFrequencyTypeId' | 'amortizationTypeId' | 'rateTypeId',
  ) => (label: string) => {
    const found = items.find((item) => item.name === label);
    if (found) change(field, String(found.id));
  };

  const needsAsset = form.withAsset && (form.assetId == null || form.assetQuantity == null);

  const submit = async () => {
    if (needsAsset) {
      setError('Para simular en otros activos, primero seleccioná un activo.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response: any = await calculateLoanInstallments({
        monetizationAmount: Number(form.monetizationAmount),
        monetizationDate: form.monetizationDate,
        dueDate: form.dueDate,
        gracePeriod: Number(form.gracePeriod),
        paymentFrequencyTypeId: Number(form.paymentFrequencyTypeId),
        amortizationTypeId: Number(form.amortizationTypeId),
        percentagePoint: Number(form.percentagePoint),
        rateTypeId: Number(form.rateTypeId),
        ...(form.withAsset && form.assetId && form.assetQuantity
          ? { assetId: form.assetId, assetQuantity: form.assetQuantity }
          : {}),
      });
      const calculatedPlans = response?.data?.plans ?? response?.plans;

      if (response?.success === false || !Array.isArray(calculatedPlans)) {
        setPlans(null);
        setError(simulatorErrorMessage(response?.message));
        return;
      }

      setPlans(calculatedPlans);
      setSelectedMode(MODES[0].key);
    } catch (requestError: any) {
      setPlans(null);
      setError(simulatorErrorMessage(
        requestError?.response?.data?.message ?? requestError?.message,
      ));
    } finally {
      setLoading(false);
    }
  };

  const current = useMemo(
    () => plans?.find((plan) => plan.grace_mode === selectedMode) ?? plans?.[0],
    [plans, selectedMode],
  );
  const selectedRate = catalogs.rate.find((item) => String(item.id) === form.rateTypeId);
  const isFixedRate = (selectedRate?.name ?? 'Fija').toLowerCase() === 'fija';
  const showAssetTab = Boolean(form.withAsset && current?.total_payable_asset != null);
  const inAsset = unit === 'asset';
  const formatter = inAsset ? asset : money;
  const unitLabel = inAsset ? form.assetName || 'activo' : localCurrencyCode;

  const cell = (row: Installment, moneyKey: keyof Installment, assetKey: keyof Installment) =>
    formatter.format(Number(row[inAsset ? assetKey : moneyKey] ?? 0));

  const planTotal = (plan: InstallmentPlan) =>
    Number(inAsset ? plan.total_payable_asset : plan.total_payable);

  const numericCell: React.CSSProperties = {
    textAlign: 'right',
    padding: '10px 12px',
    fontSize: 14,
    whiteSpace: 'nowrap',
  };
  const taxCell: React.CSSProperties = { ...numericCell, background: COLORS.taxColumn };

  return (
    <Card width='100%'>
      <Breadcrumb prevRoute='Monetización' currentRoute='Simulador de cuotas' />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 340px) minmax(0, 1fr)',
          gap: 24,
          alignItems: 'start',
          marginTop: 16,
        }}
      >
        <Card
          border
          spacing='4'
          width='100%'
          style={SURFACE_CARD}
        >
          <H type='4' variant='neutral' modifier='strong' weight='bold' withMargin={false}>
            Simulá tu plan de cuotas
          </H>
          <P type='4' variant='neutral' modifier='weak' withMargin={false}>
            Completá los datos del préstamo.
          </P>

          <Card margin={{ top: '4' }}>
            <Input
              id='monetization-amount'
              label={`Monto de monetización (${localCurrencyCode})`}
              size='md'
              onlyNumbers
              value={form.monetizationAmount}
              onChange={(event) => change('monetizationAmount', event.target.value)}
            />
          </Card>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 16,
              alignItems: 'end',
            }}
          >
            <Card>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha de monetización
              </P>
              <Card margin={{ top: '2' }}>
                <DatePicker
                  defaultValue={form.monetizationDate}
                  onChange={(date: string) => change('monetizationDate', date)}
                  disabled={false}
                  displayFormat='DD/MM/AA'
                />
              </Card>
            </Card>
            <Card>
              <P withMargin={false} variant='neutral' type='4' modifier='strong'>
                Fecha de vencimiento
              </P>
              <Card margin={{ top: '2' }}>
                <DatePicker
                  defaultValue={form.dueDate}
                  onChange={(date: string) => change('dueDate', date)}
                  disabled={false}
                  displayFormat='DD/MM/AA'
                />
              </Card>
            </Card>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 16,
              alignItems: 'end',
            }}
          >
            <Input
              id='grace-period'
              label='Período de gracia (días)'
              size='md'
              onlyNumbers
              value={form.gracePeriod}
              onChange={(event) => change('gracePeriod', event.target.value)}
            />
            <Input
              id='percentage-point'
              label='Tasa TNA %'
              size='md'
              value={form.percentagePoint}
              onChange={(event) => change('percentagePoint', event.target.value)}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 16,
              alignItems: 'end',
            }}
          >
            <DropDown
              id='payment-frequency'
              label='Frecuencia de pago'
              size='md'
              title={catalogName(catalogs.frequency, form.paymentFrequencyTypeId)}
              items={catalogs.frequency.map((item) => ({ label: item.name }))}
              onChange={selectCatalog(catalogs.frequency, 'paymentFrequencyTypeId')}
            />
            <DropDown
              id='amortization-type'
              label='Sistema de amortización'
              size='md'
              title={catalogName(catalogs.amortization, form.amortizationTypeId)}
              items={catalogs.amortization.map((item) => ({ label: item.name }))}
              onChange={selectCatalog(catalogs.amortization, 'amortizationTypeId')}
            />
          </div>

          <Card margin={{ top: '4' }}>
            <DropDown
              id='rate-type'
              label='Tipo de tasa'
              size='md'
              title={catalogName(catalogs.rate, form.rateTypeId)}
              items={catalogs.rate.map((item) => ({ label: item.name }))}
              onChange={selectCatalog(catalogs.rate, 'rateTypeId')}
            />
            <Card margin={{ top: '2' }}>
              <P type='5' variant='neutral' modifier='weak' withMargin={false}>
                Este simulador calcula siempre con tasa fija, usando la TNA que ingresaste arriba.
              </P>
            </Card>
          </Card>

          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 8,
              boxSizing: 'border-box',
              border: `1px solid ${form.withAsset ? COLORS.accent : COLORS.border}`,
              background: form.withAsset ? COLORS.accentSoft : COLORS.surface,
            }}
          >
            <CheckBox
              id='asset-toggle'
              checked={form.withAsset}
              labelWeight='bold'
              onChange={(checked: boolean) => change('withAsset', checked)}
            >
              Carga en otros activos
            </CheckBox>
            <Card margin={{ top: '1' }}>
              <P type='5' variant='neutral' modifier='weak' withMargin={false}>
                Agregá un segundo plan de cuotas, expresado en unidades de un activo.
              </P>
            </Card>

            {form.withAsset && (
              <Card margin={{ top: '3' }}>
                <Button
                  variant='tertiary'
                  size='sm'
                  fullWidth
                  onClick={() => setAssetModalOpen(true)}
                >
                  {form.assetName ? `Activo: ${form.assetName}` : 'Seleccionar activo'}
                </Button>
                {form.assetName ? (
                  <Card margin={{ top: '2' }}>
                    <P type='5' variant='neutral' modifier='strong' withMargin={false}>
                      Cantidad de activo: {asset.format(form.assetQuantity ?? 0)}
                    </P>
                    <P type='5' variant='neutral' modifier='weak' withMargin={false}>
                      La cotización es solo informativa: no afecta el cálculo de las cuotas.
                    </P>
                  </Card>
                ) : (
                  <Card margin={{ top: '2' }}>
                    <P type='5' variant='neutral' modifier='weak' withMargin={false}>
                      Seleccioná un activo para incluir el segundo plan en unidades.
                    </P>
                  </Card>
                )}
              </Card>
            )}
          </div>

          <Card margin={{ top: '4' }}>
            <Button
              id='simulate-button'
              variant='primary'
              size='lg'
              fullWidth
              disabled={loading || needsAsset}
              onClick={submit}
            >
              {loading ? 'Calculando…' : 'Simular plan de cuotas'}
            </Button>
          </Card>
        </Card>

        <div>
          {error && (
            <Card
              border
              spacing='3'
              margin={{ bottom: '3' }}
              width='100%'
              style={{ ...SURFACE_CARD, border: '1px solid #E4B95B', background: '#FFF8E6' }}
            >
              <div role='alert'>
                <P type='3' variant='neutral' modifier='strong' withMargin={false}>
                  {error}
                </P>
              </div>
            </Card>
          )}

          {!current && !error && (
            <Card
              border
              spacing='4'
              width='100%'
              style={SURFACE_CARD}
            >
              <Card display='grid' justifyContent='center' margin={{ top: '11', bottom: '11' }}>
                <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
                  <Image name='folder_empty' />
                </Card>
                <P variant='primary' align='center' withMargin={false} weight='bold'>
                  Todavía no generaste ninguna simulación
                </P>
                <P variant='primary' align='center'>
                  Completá los datos del préstamo a la izquierda y tocá “Simular plan de cuotas”
                  para ver el detalle de cuotas, intereses e impuestos.
                </P>
              </Card>
            </Card>
          )}

          {current && (
            <>
              {!isFixedRate && (
                <Card
                  border
                  spacing='3'
                  margin={{ bottom: '3' }}
                  width='100%'
                  style={{ ...SURFACE_CARD, border: '1px solid #E4B95B', background: '#FFF8E6' }}
                >
                  <P type='4' variant='neutral' modifier='strong' withMargin={false}>
                    {selectedRate?.name} es informativa; el cálculo usa la TNA fija ingresada.
                  </P>
                </Card>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                {MODES.map((mode) => {
                  const plan = plans?.find((item) => item.grace_mode === mode.key);
                  if (!plan) return null;
                  const isSelected = plan.grace_mode === current.grace_mode;

                  return (
                    <div
                      key={mode.key}
                      data-testid='mode-card'
                      role='radio'
                      tabIndex={0}
                      aria-checked={isSelected}
                      aria-label={mode.label}
                      onClick={() => setSelectedMode(mode.key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') setSelectedMode(mode.key);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 8,
                        padding: 12,
                        boxSizing: 'border-box',
                        border: `1px solid ${isSelected ? COLORS.accent : COLORS.border}`,
                        background: isSelected ? COLORS.accentSoft : '#FFFFFF',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>
                          {mode.order}
                        </span>
                        <span style={{ fontSize: 12, color: COLORS.muted }}>
                          {plan.total_cuotas} cuotas
                        </span>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>
                        {mode.label}
                      </div>
                      <div style={{ fontSize: 13, color: COLORS.muted, marginTop: 4 }}>
                        Total: {formatter.format(planTotal(plan))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 12,
                  margin: '12px 0',
                }}
              >
                <Kpi label='Cuotas' value={String(current.total_cuotas)} />
                <Kpi
                  label='Interés total'
                  value={formatter.format(Number(
                    inAsset ? current.total_interest_asset : current.total_interest,
                  ))}
                />
                <Kpi
                  label='Impuestos total'
                  value={formatter.format(inAsset
                    ? Number(current.total_iva_on_interest_asset)
                      + Number(current.total_tax_on_capital_asset)
                    : current.total_taxes)}
                />
                <Kpi
                  label={`Total a pagar (${unitLabel})`}
                  value={formatter.format(planTotal(current))}
                />
              </div>

              <Card
                border
                width='100%'
                style={{ ...SURFACE_CARD, overflow: 'hidden' }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 20,
                    padding: '12px 16px',
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <TabButton
                    active={!inAsset}
                    label={`Plan en moneda (${localCurrencyCode})`}
                    onClick={() => setUnit('money')}
                  />
                  {showAssetTab && (
                    <TabButton
                      active={inAsset}
                      label='Plan en activo'
                      onClick={() => setUnit('asset')}
                    />
                  )}
                </div>

                <div style={{ padding: '8px 16px', color: COLORS.muted, fontSize: 12 }}>
                  Impuestos aplicados por el sistema: IVA {current.iva_rate_applied}% sobre interés
                  {' '}y {current.tax_on_capital_rate_applied}% sobre amortización.
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: COLORS.headerBg }}>
                        <th style={{ ...numericCell, textAlign: 'left', fontWeight: 700 }}>#</th>
                        <th style={{ ...numericCell, textAlign: 'left', fontWeight: 700 }}>
                          Vencimiento
                        </th>
                        <th style={{ ...numericCell, fontWeight: 700 }}>Amortización</th>
                        <th style={{ ...numericCell, fontWeight: 700 }}>Interés</th>
                        <th style={{ ...taxCell, fontWeight: 700 }}>Imp. Interés</th>
                        <th style={{ ...taxCell, fontWeight: 700 }}>Imp. Capital</th>
                        <th style={{ ...numericCell, fontWeight: 700 }}>Cuota total</th>
                        <th style={{ ...numericCell, fontWeight: 700 }}>Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.installments.map((row) => (
                        <tr
                          key={`${row.installment_number}-${row.due_date}-${row.grace_only}`}
                          style={{
                            borderTop: `1px solid ${COLORS.border}`,
                            background: row.grace_only ? COLORS.graceRow : undefined,
                          }}
                        >
                          <td style={{ ...numericCell, textAlign: 'left' }}>
                            {row.grace_only ? 'G' : row.installment_number}
                            {row.grace_only && (
                              <span style={{ marginLeft: 6, fontSize: 12, color: COLORS.muted }}>
                                Cuota de gracia
                              </span>
                            )}
                          </td>
                          <td style={{ ...numericCell, textAlign: 'left' }}>
                            {formatDate(row.due_date)}
                          </td>
                          <td style={numericCell}>{cell(row, 'amortization', 'amortization_asset')}</td>
                          <td style={numericCell}>{cell(row, 'interest', 'interest_asset')}</td>
                          <td style={taxCell}>{cell(row, 'iva_on_interest', 'iva_on_interest_asset')}</td>
                          <td style={taxCell}>{cell(row, 'tax_on_capital', 'tax_on_capital_asset')}</td>
                          <td style={{ ...numericCell, fontWeight: 700 }}>
                            {cell(row, 'installment_total', 'installment_total_asset')}
                          </td>
                          <td style={numericCell}>
                            {cell(row, 'remaining_amount', 'remaining_amount_asset')}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: `2px solid ${COLORS.border}`, fontWeight: 700 }}>
                        <td style={{ ...numericCell, textAlign: 'left' }} colSpan={2}>Total</td>
                        <td style={numericCell}>
                          {formatter.format(Number(
                            inAsset ? current.total_amortization_asset : current.total_amortization,
                          ))}
                        </td>
                        <td style={numericCell}>
                          {formatter.format(Number(
                            inAsset ? current.total_interest_asset : current.total_interest,
                          ))}
                        </td>
                        <td style={taxCell}>
                          {formatter.format(Number(
                            inAsset
                              ? current.total_iva_on_interest_asset
                              : current.total_iva_on_interest,
                          ))}
                        </td>
                        <td style={taxCell}>
                          {formatter.format(Number(
                            inAsset
                              ? current.total_tax_on_capital_asset
                              : current.total_tax_on_capital,
                          ))}
                        </td>
                        <td style={numericCell}>{formatter.format(planTotal(current))}</td>
                        <td style={numericCell}>-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 20,
                    padding: '12px 16px',
                    color: COLORS.muted,
                    fontSize: 12,
                  }}
                >
                  <Legend color={COLORS.graceRow} label='Cuota de gracia (solo interés)' />
                  <Legend color={COLORS.taxColumn} label='Columnas de impuesto' />
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      <AssetSelectionModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        onAccept={(selected) => {
          setForm((currentForm) => ({
            ...currentForm,
            assetId: selected.assetId,
            assetName: selected.assetName,
            assetQuantity: selected.quantity,
            assetQuotation: selected.quotation,
          }));
          setAssetModalOpen(false);
          invalidate();
        }}
        currencyCode={localCurrencyCode}
        initialAssetId={form.assetId}
        initialQuantity={form.assetQuantity}
        initialQuotation={form.assetQuotation}
      />
    </Card>
  );
};

const Kpi = ({ label, value }: { label: string; value: string }) => (
  <Card
    border
    spacing='3'
    width='100%'
              style={SURFACE_CARD}
            >
    <P type='5' variant='neutral' modifier='weak' withMargin={false}>
      {label}
    </P>
    <Card margin={{ top: '1' }}>
      <H type='4' variant='neutral' modifier='strong' weight='bold' withMargin={false}>
        {value}
      </H>
    </Card>
  </Card>
);

const TabButton = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type='button'
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      padding: '4px 0',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: active ? 700 : 400,
      color: active ? COLORS.accent : COLORS.muted,
      borderBottom: `2px solid ${active ? COLORS.accent : 'transparent'}`,
    }}
  >
    {label}
  </button>
);

const Legend = ({ color, label }: { color: string; label: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: color,
        border: `1px solid ${COLORS.border}`,
      }}
    />
    {label}
  </span>
);

export default LoanInstallmentSimulatorPage;
